import { type Opportunity } from "@repo/api";
import { isDefined } from "@repo/utils";

import type {
  Design,
  NormalizedDrawing,
  OpportunityDrawing,
  PlannerDetail,
} from "./types";

const isOpportunityDrawing = (
  planner: OpportunityDrawing | PlannerDetail | undefined
): planner is OpportunityDrawing =>
  planner ? Object.prototype.hasOwnProperty.call(planner, "plannerId") : false;

const DrawingTypes = [
  "BESTA",
  "PLATSA",
  "STUVA",
  "PAX",
  "NORDLI",
  "BROR",
  "EKET",
  "JONAXEL",
  "BOAXEL",
  "SMASTAD",
  "KALLAX",
  "IVAR",
  "VALLENTUNA",
  "VIMLE",
  "SODERHAMN",
  "LIDHULT",
  "GRONLID",
];

const getVPCPlannerImage = (planner: OpportunityDrawing) => {
  if (
    planner.designImage?.includes("generic") &&
    DrawingTypes.includes(planner.plannerType.toUpperCase())
  ) {
    return `https://ipexdesignsearch.ikea.com/images/${planner.plannerType}.png`;
  }

  return (
    planner.designImage ??
    "https://ipexdesignsearch.ikea.com/images/default.png"
  );
};

export const getDrawings = (
  planners: Opportunity["planners"] | undefined
): NormalizedDrawing[] => {
  return [...(planners?.plannersInfo ?? []).map((d) => normalizeDrawing(d))]
    .filter(isDefined)
    .sort((a, b) => {
      return (
        new Date(b.updated ?? "").getTime() -
        new Date(a.updated ?? "").getTime()
      );
    });
};

const normalizeDrawing = (
  drawing: OpportunityDrawing | PlannerDetail
): NormalizedDrawing => {
  if (isOpportunityDrawing(drawing)) {
    return {
      id: drawing.plannerId,
      image: getVPCPlannerImage(drawing),
      name: drawing.plannerId,
      value: drawing.amount,
      created: drawing.updatedTime,
      updated: drawing.updatedTime,
      type: drawing.plannerType,
      statusDescription: undefined,
      editUrl: undefined,
      viewUrl: undefined,
      isVPCDrawing: true,
      items: drawing.items,
      statusCodeTranslated: drawing.statusCodeTranslated,
      statusCodeNotTranslated: "DEFAULT" as const,
      applicationId: drawing.applicationId,
      addedToProjectValue: drawing.isIncludedInOppValue,
    };
  } else {
    return {
      id: drawing.designId,
      image: drawing.thumbnailUrl,
      name: drawing.designName,
      value: Number(drawing.totalprice),
      created: drawing.createdDate,
      updated: drawing.lastModifiedDate,
      type: drawing.plannerType,
      statusDescription: drawing.statusDescription,
      editUrl: drawing.designUrlEdit,
      viewUrl: drawing.designUrlView,
      isVPCDrawing: false,
      statusCodeTranslated: drawing.statusCodeTranslated,
      statusCodeNotTranslated: drawing.statusCodeNotTranslated,
      applicationId: drawing.applicationId,
      addedToProjectValue: drawing.isIncludedInOppValue,
    };
  }
};

export const isKitchenPlannerIhp3Spec = (id?: string) => {
  if (!id) {
    return false;
  }

  const value = id.replace(/-/gi, "");
  return (
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}$/gim.test(value.trim()) ||
    (/[A-Z0-9]{8}?[A-Z0-9]{4}?[A-Z0-9]{4}-?[A-Z0-9]{4}-?[A-Z0-9]{12}/gim.test(
      value.toUpperCase().trim()
    ) &&
      value.length === 32)
  );
};

export const isKitchenPlannerIhp2Spec = (id?: string) => {
  if (!id) {
    return false;
  }

  const value = id.replace(/-/gi, "");

  return (
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+.[A-Z]{2,4}$/gim.test(value.trim()) ||
    isOfficePlannerIdIhp2Spec(id) ||
    (/[0-9]{4}?[0-9]{4}?[0-9]{4}/gim.test(value.trim()) && value.length === 12)
  );
};

export const isOfficePlannerIdIhp2Spec = (id?: string) => {
  if (!id) {
    return false;
  }

  return /^[ A-Za-z0-9_@./#&+-]*$/.test(id) && id.length === 11;
};

export const isDexfPlannerId = (id?: string) => {
  if (!id) {
    return false;
  }
  const value = id.replace(/-/gi, "");
  return /^[A-F0-9]{32}$/i.test(value);
};

export const getThumbnailImage = (design: Design) => {
  if (design.designImageThumbnailUrl) {
    return design.designImageThumbnailUrl;
  }

  if (
    design.plannerType &&
    design.designImageUrl?.includes("generic") &&
    DrawingTypes.includes(design.plannerType.toUpperCase())
  ) {
    return `https://ipexdesignsearch.ikea.com/images/${design.plannerType}.png`;
  }

  return (
    design.designImageUrl ??
    "https://ipexdesignsearch.ikea.com/images/default.png"
  );
};
